/**
 * Test data extraction — parses file-based payloads (v2) and legacy inline payloads (v1).
 *
 * In v2, heavy data (network requests, console logs, API calls) is referenced
 * by file path rather than embedded inline. The reporter moves these files to
 * the report directory without loading them into memory.
 */

import type {
  NavigationAnnotation,
  ApiAssertion,
  ApiCallsByStatusRange,
} from '@testrelic/core';
import { isTestRelicFilePayload, isTestRelicDataPayload, ATTACHMENT_NAME } from '@testrelic/core';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface AttachmentLike {
  name: string;
  contentType: string;
  body?: Buffer;
}

interface AnnotationLike {
  type: string;
  description?: string;
}

/** Extracted data with file references for heavy data. */
export interface ExtractedTestData {
  navigations: NavigationAnnotation[];
  apiAssertions: ApiAssertion[];
  /** Path to JSONL file with network requests, or null */
  networkRequestsFile: string | null;
  networkRequestsCount: number;
  /** Path to JSONL file with console logs, or null */
  consoleLogsFile: string | null;
  consoleLogsCount: number;
  /** Path to JSONL file with API calls, or null */
  apiCallsFile: string | null;
  apiCallsCount: number;
  /** Response-status mix of those calls, tallied at capture time. */
  apiStatusMix: ApiCallsByStatusRange | null;
}

/** Sum two status mixes. A test can attach more than one payload. */
function addStatusMix(
  a: ApiCallsByStatusRange | null,
  b: ApiCallsByStatusRange | null | undefined,
): ApiCallsByStatusRange | null {
  if (!b) return a;
  if (!a) return { ...b };
  return {
    '2xx': a['2xx'] + b['2xx'],
    '3xx': a['3xx'] + b['3xx'],
    '4xx': a['4xx'] + b['4xx'],
    '5xx': a['5xx'] + b['5xx'],
    error: a.error + b.error,
  };
}

// ---------------------------------------------------------------------------
// Extraction
// ---------------------------------------------------------------------------

/**
 * Extract test data from Playwright attachments.
 *
 * Supports both v2 file-based payloads (preferred) and v1 inline payloads (legacy fallback).
 * For v1 payloads, data is still inline but wrapped in the same ExtractedTestData shape
 * with file paths set to null.
 */
export function extractTestData(
  attachments: readonly AttachmentLike[],
  annotations: readonly AnnotationLike[],
  testTitle: string,
  isSkipped: boolean,
): ExtractedTestData {
  let navigations: NavigationAnnotation[] = [];
  let apiAssertions: ApiAssertion[] = [];
  let networkRequestsFile: string | null = null;
  let networkRequestsCount = 0;
  let consoleLogsFile: string | null = null;
  let consoleLogsCount = 0;
  let apiCallsFile: string | null = null;
  let apiCallsCount = 0;
  let apiStatusMix: ApiCallsByStatusRange | null = null;

  const payloadAttachments = attachments.filter(
    (a) => a.name === ATTACHMENT_NAME && a.body,
  );

  let attachmentParsed = false;
  if (payloadAttachments.length > 0) {
    for (const att of payloadAttachments) {
      try {
        const parsed = JSON.parse(att.body!.toString());

        // v2 file-based payload (preferred)
        if (isTestRelicFilePayload(parsed)) {
          navigations = navigations.concat(parsed.navigations);
          apiAssertions = apiAssertions.concat(parsed.apiAssertions);
          if (parsed.networkRequestsFile) {
            networkRequestsFile = parsed.networkRequestsFile;
            networkRequestsCount += parsed.networkRequestsCount;
          }
          if (parsed.consoleLogsFile) {
            consoleLogsFile = parsed.consoleLogsFile;
            consoleLogsCount += parsed.consoleLogsCount;
          }
          if (parsed.apiCallsFile) {
            apiCallsFile = parsed.apiCallsFile;
            apiCallsCount += parsed.apiCallsCount;
          }
          apiStatusMix = addStatusMix(apiStatusMix, parsed.apiStatusMix);
          attachmentParsed = true;
          continue;
        }

        // v1 inline payload (legacy fallback)
        if (isTestRelicDataPayload(parsed)) {
          navigations = navigations.concat(parsed.navigations);
          if (Array.isArray(parsed.apiAssertions)) {
            apiAssertions = apiAssertions.concat(parsed.apiAssertions as ApiAssertion[]);
          }
          // v1 data is inline — counts tracked but no files
          networkRequestsCount += parsed.networkRequests?.length ?? 0;
          apiCallsCount += parsed.apiCalls?.length ?? 0;
          // v1 payloads carried consoleLogs inline; the interface never
          // declared it, so this is a probe of a legacy shape.
          const payloadLogs = (parsed as unknown as Record<string, unknown>).consoleLogs;
          if (Array.isArray(payloadLogs)) {
            consoleLogsCount += payloadLogs.length;
          }
          attachmentParsed = true;
        }
      } catch {
        process.stderr.write(`[testrelic] Warning: Corrupt attachment for test "${testTitle}"\n`);
      }
    }
  }

  if (!attachmentParsed) {
    // Fallback: read navigations from legacy annotations
    navigations = annotations
      .filter((a) => a.type === 'lambdatest-navigation' && a.description)
      .map((a) => {
        try { return JSON.parse(a.description!) as NavigationAnnotation; } catch { return null; }
      })
      .filter((n): n is NavigationAnnotation => n !== null);
  }

  const hasNoData = navigations.length === 0 && networkRequestsCount === 0 && apiCallsCount === 0;
  if (hasNoData && !isSkipped && !attachmentParsed) {
    process.stderr.write(
      `[testrelic] Warning: No data found for test "${testTitle}". ` +
      `Make sure your tests import { test, expect } from '@testrelic/playwright-analytics/fixture' ` +
      `instead of '@playwright/test'. Without the TestRelic fixture, network logs, video sync, ` +
      `and Test Navigation data cannot be captured.\n`,
    );
  }

  return {
    navigations,
    apiAssertions,
    networkRequestsFile,
    networkRequestsCount,
    consoleLogsFile,
    consoleLogsCount,
    apiCallsFile,
    apiCallsCount,
    apiStatusMix,
  };
}
